import axios, { AxiosRequestConfig } from 'axios';
import env from '../../config/env';
import { isEmbeddedMode } from '../../utils/embeddedMode';
import { getDriverTokenManager } from '../../utils/tokenManager';
import { devLog, devWarn } from '../../utils/devLog';

const baseURL = env.apiUrl;

devLog('[API] Initializing API client');

const api = axios.create({
  baseURL,
  withCredentials: true,
});

/**
 * Retrieve a valid token from the DriverTokenManager.
 * Falls back to localStorage for embedded mode when the manager has no fetcher yet.
 */
async function getToken(): Promise<string | null> {
  const token = await getDriverTokenManager().getToken();
  if (token) return token;

  // Fallback: read directly from localStorage (embedded mode before manager is wired)
  const stored = localStorage.getItem('access_token');
  return stored ?? null;
}

api.interceptors.request.use(
  async (config) => {
    devLog(
      `[API] Making ${config.method?.toUpperCase()} request to: ${config.url}`
    );

    // A token the caller passed wins. Accepting an invitation passes the
    // new login's own; replacing it with the cached one, which can be a
    // previous login's, got "Sign in to accept this invitation" over and over.
    if (config.headers.Authorization) return config;

    const token = await getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      devLog('[API] Auth token attached');
    } else {
      devLog('[API] No auth token available');
    }
    return config;
  },
  (error) => {
    console.error('[API] Request error:', error);
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    devLog(`[API] Response from ${response.config.url}:`, {
      status: response.status,
      statusText: response.statusText,
    });
    return response;
  },
  async (error) => {
    console.error('[API] Response error:', error.message);

    if (error.response) {
      console.error('[API] Error response:', {
        url: error.config?.url,
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
      });

      const isInvitationEndpoint =
        error.config?.url?.includes('/driver-invitations/validate') ||
        error.config?.url?.includes('/driver-invitations/accept');

      if (error.response.status === 401 && !isInvitationEndpoint) {
        const originalRequest = error.config as AxiosRequestConfig & {
          _retried?: boolean;
        };

        if (!originalRequest._retried) {
          originalRequest._retried = true;
          devLog('[API] 401 received, attempting token refresh and retry');

          if (isEmbeddedMode()) {
            // In embedded mode: ask native to refresh, then wait for the new
            // token to appear in localStorage before retrying once.
            devLog(
              '[API] Embedded mode — requesting token refresh from native'
            );
            if (window.ReactNativeWebView) {
              window.ReactNativeWebView.postMessage(
                JSON.stringify({ type: 'requestTokenRefresh' })
              );
            }

            const newToken = await waitForNewToken(
              localStorage.getItem('access_token')
            );

            if (newToken) {
              devLog('[API] New token received, retrying request');
              originalRequest.headers = originalRequest.headers ?? {};
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return api.request(originalRequest);
            }

            devWarn(
              '[API] Embedded mode — no new token received, letting native handle logout'
            );
            return Promise.reject(error);
          }

          // Web mode: use TokenManager force refresh
          const newToken = await getDriverTokenManager().forceRefresh();
          if (newToken) {
            devLog('[API] Token refreshed, retrying request');
            originalRequest.headers = originalRequest.headers ?? {};
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return api.request(originalRequest);
          }

          devWarn('[API] Token refresh failed, redirecting to login');
          localStorage.removeItem('access_token');
          window.location.href = '/';
          return Promise.reject(error);
        }

        // Second 401 after retry — clear token and redirect
        devWarn('[API] Unauthorized after retry, redirecting to login');
        localStorage.removeItem('access_token');

        if (!isEmbeddedMode()) {
          window.location.href = '/';
        }
      } else if (isInvitationEndpoint) {
        devLog('[API] Skipping login redirect for invitation endpoint');
      }
    } else if (error.request) {
      console.error('[API] No response received:', error.request);
    }

    return Promise.reject(error);
  }
);

/**
 * Poll localStorage for a new access_token after requesting a native refresh.
 * Returns the new token once it differs from the previous value, or null on timeout.
 */
function waitForNewToken(
  previousToken: string | null,
  timeoutMs = 10_000,
  intervalMs = 200
): Promise<string | null> {
  return new Promise((resolve) => {
    const deadline = Date.now() + timeoutMs;

    const check = () => {
      const current = localStorage.getItem('access_token');
      if (current && current !== previousToken) {
        getDriverTokenManager().updateCachedToken(current);
        resolve(current);
        return;
      }
      if (Date.now() >= deadline) {
        resolve(null);
        return;
      }
      setTimeout(check, intervalMs);
    };

    // Also listen for the storage event (cross-context injection)
    const onStorage = (e: StorageEvent) => {
      if (
        e.key === 'access_token' &&
        e.newValue &&
        e.newValue !== previousToken
      ) {
        window.removeEventListener('storage', onStorage);
        getDriverTokenManager().updateCachedToken(e.newValue);
        resolve(e.newValue);
      }
    };
    window.addEventListener('storage', onStorage);

    check();
  });
}

export default api;
